/**
 * Tenant configuration for multi-tenant application support. Default tenant is assumed to be 'community'.
 * Each tenant is identified by subdomains and has a unique key.
 */
import { Environment } from 'src/constants';
import { getEnvironment } from 'src/services/http/user';

interface Tenant {
  key: string;
  subdomains: readonly string[];
  baseUrl: Record<DeployedEnvironment, string>;
  adobe: {
    id: Record<DeployedEnvironment, number>;
  };
}

// Environments that are actually deployed (excludes LOCAL).
// getEnvironment() never returns LOCAL, so we only need these three environments in the config.
export type DeployedEnvironment = Exclude<Environment, Environment.LOCAL>;

/**
 * Available tenants in the application.
 * Maps tenant keys to their configuration including supported subdomains.
 */
export const TENANTS = {
  COMMUNITY: {
    key: 'community',
    subdomains: ['community', 'community-ui', 'learning'],
    baseUrl: {
      [Environment.DEVELOPMENT]: 'https://community.dev.atlassian.com/learning',
      [Environment.STAGING]: 'https://community.stg.atlassian.com/learning',
      [Environment.PRODUCTION]: 'https://community.atlassian.com/learning',
    },
    adobe: {
      id: {
        [Environment.DEVELOPMENT]: 136492,
        [Environment.STAGING]: 136492,
        [Environment.PRODUCTION]: 135642,
      },
    },
  },
  ENABLEMENT_ACADEMY: {
    key: 'enablementAcademy',
    subdomains: ['academy'],
    baseUrl: {
      [Environment.DEVELOPMENT]: 'https://academy.dev.atlassian.com',
      [Environment.STAGING]: 'https://academy.stg.atlassian.com',
      [Environment.PRODUCTION]: 'https://academy.atlassian.com',
    },
    adobe: {
      id: {
        [Environment.DEVELOPMENT]: 136474,
        [Environment.STAGING]: 136474,
        [Environment.PRODUCTION]: 136472,
      },
    },
  },
} as const;

/**
 * Extracts the first subdomain from a hostname.
 * @param hostname - The full hostname (e.g., "community.dev.atlassian.com")
 * @returns The first subdomain (eg. "community") or null if not found
 */
export const getSubdomainFromHostname = (hostname: string): string | null => {
  const parts = hostname.split('.');

  return parts.length > 2 ? parts[0] : null;
};

/**
 * Determines the current tenant based on the hostname. Works in both SSR and client-side environments.
 * @returns The current tenant or null if not found
 */
export const getTenant = (): Tenant | null => {
  const hostname = globalThis.location?.hostname;
  const subdomain = hostname ? getSubdomainFromHostname(hostname) : null;

  if (!subdomain) {
    return null;
  }

  return (Object.values(TENANTS) as Tenant[]).find((tenant) => tenant.subdomains.includes(subdomain)) || null;
};

/**
 * Gets the base URL for a tenant.
 * @param tenant - Optional tenant to use. If not provided, uses the current tenant from getTenant().
 * @returns The base URL for the tenant
 */
export const getTenantBaseUrl = (tenant?: Tenant | null): string | undefined => {
  const targetTenant = tenant ?? getTenant();

  return targetTenant?.baseUrl[getEnvironment() as DeployedEnvironment];
};

/**
 * Gets the Adobe Learning Manager environment or account ID for a tenant.
 * @param tenant - Optional tenant to use. If not provided, uses the current tenant from getTenant().
 * @returns The Adobe environment ID for the tenant
 */
export const getAdobeEnvironmentId = (tenant?: Tenant | null): number | undefined => {
  const targetTenant = tenant ?? getTenant();

  return targetTenant?.adobe.id[getEnvironment() as DeployedEnvironment];
};
